import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { useCreateCustomer, useUpdateCustomer } from '@/hooks/useCustomers';
import { usePermissions } from '@/hooks/usePermissions';
import { useRequestCustomerEdit, useRequestsFor } from '@/hooks/useApprovals';
import { useAuth } from '@/hooks/useAuth';
import type { Customer } from '@/types/domain';
import { checkPhone, formatPhone } from '@/lib/phone';

export interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  /** When provided, the form is in "edit" mode. */
  customer?: Customer | null;
  /** Fired after a successful create — gives the parent the new customer id
   *  so it can immediately select the customer (e.g. in POS). */
  onCreated?: (id: string) => void;
}

// Phone shape is decided by lib/phone.ts, not by a regex here. Anything a
// human might paste is accepted and converted; what cannot become a reachable
// number is refused with the reason, because a bad number is invisible later —
// Meta answers "sent" and the message simply never arrives.
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const schema = z.object({
  name: z.string().trim().min(1, 'Nama wajib diisi').max(120, 'Nama terlalu panjang'),
  // Loose on purpose: NIK is 16 digits, NPWP is 15 or 16 and usually written
  // with dots and a dash. Enforcing either shape would reject the other, and a
  // customer record is worth more than a perfectly formatted tax number.
  taxId: z.string().trim().max(32, 'NIK/NPWP terlalu panjang').optional().or(z.literal('')),
  address: z.string().trim().max(300, 'Alamat terlalu panjang').optional().or(z.literal('')),
  company: z
    .string()
    .trim()
    .max(160, 'Nama perusahaan terlalu panjang')
    .optional()
    .or(z.literal('')),
  phone: z
    .string()
    .trim()
    .max(24, 'No telp terlalu panjang')
    .superRefine((v, ctx) => {
      const { problem } = checkPhone(v);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    }),
  email: z
    .string()
    .trim()
    .max(160, 'Email terlalu panjang')
    .refine(
      (v) => v.length === 0 || emailRegex.test(v),
      'Format email tidak valid',
    )
    .optional()
    .or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

export function CustomerForm({ open, onClose, customer, onCreated }: CustomerFormProps) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const isEdit = !!customer;
  // Without customers.edit the form does not save: it files a request that an
  // approver applies (migration 0031). Same fields, different button.
  const asRequest = isEdit && !can('customers', 'edit');
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const requestEdit = useRequestCustomerEdit();
  const { data: editRequests } = useRequestsFor('customer.edit', 'customer_id', asRequest ? customer?.id : null);
  const pendingRequest = editRequests?.find((r) => r.status === 'pending') ?? null;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      company: '',
      phone: '',
      email: '',
      taxId: '',
      address: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    if (customer) {
      reset({
        name: customer.name,
        company: customer.company ?? '',
        phone: customer.phone,
        email: customer.email ?? '',
        taxId: customer.taxId ?? '',
        address: customer.address ?? '',
      });
    } else {
      reset({ name: '', company: '', phone: '', email: '', taxId: '', address: '' });
    }
  }, [open, customer, reset]);

  const onSubmit = async (values: FormValues) => {
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      // Surface zod errors on the form (defence-in-depth — RHF required rules cover most cases).
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormValues | undefined;
        if (key) setError(key, { type: 'validate', message: issue.message });
      }
      return;
    }

    if (!user) return;
    setSubmitError(null);

    if (asRequest && customer) {
      // Only what changed; the server refuses an empty diff and normalises
      // the phone the same way checkPhone() does.
      const proposed: Record<string, string> = {
        name: parsed.data.name,
        phone: parsed.data.phone,
        company: parsed.data.company ?? '',
        email: parsed.data.email ?? '',
        tax_id: parsed.data.taxId ?? '',
        address: parsed.data.address ?? '',
      };
      const current: Record<string, string> = {
        name: customer.name,
        phone: customer.phone,
        company: customer.company ?? '',
        email: customer.email ?? '',
        tax_id: customer.taxId ?? '',
        address: customer.address ?? '',
      };
      const changes: Record<string, string> = {};
      for (const k of Object.keys(proposed)) {
        const a = k === 'phone' ? (checkPhone(proposed[k]).e164 ?? proposed[k]) : proposed[k].trim();
        if (a !== current[k]) changes[k] = a;
      }
      if (Object.keys(changes).length === 0) {
        setSubmitError('Tidak ada yang berubah.');
        return;
      }
      try {
        await requestEdit.mutateAsync({ customerId: customer.id, changes });
      } catch (e) {
        setSubmitError(e instanceof Error ? e.message : 'Gagal mengajukan perubahan');
        return;
      }
      onClose();
      return;
    }

    if (isEdit && customer) {
      await update.mutateAsync({
        id: customer.id,
        input: {
          name: parsed.data.name,
          company: parsed.data.company || undefined,
          phone: checkPhone(parsed.data.phone).e164 ?? parsed.data.phone,
          email: parsed.data.email || undefined,
          taxId: parsed.data.taxId || null,
          address: parsed.data.address || null,
        },
      });
    } else {
      const newId = await create.mutateAsync({
        name: parsed.data.name,
        company: parsed.data.company || undefined,
        phone: checkPhone(parsed.data.phone).e164 ?? parsed.data.phone,
        email: parsed.data.email || undefined,
        taxId: parsed.data.taxId || null,
        address: parsed.data.address || null,
        createdBy: user.uid,
      });
      onCreated?.(newId);
    }
    onClose();
  };

  // Live feedback under the field: what will actually be saved, and a warning
  // when the number is valid but WhatsApp cannot reach it.
  const phoneValue = watch('phone');
  const phoneCheck = checkPhone(phoneValue ?? '');
  const phoneHint = !phoneValue?.trim()
    ? 'Boleh ketik 0811…, atau tempel langsung dari WhatsApp (+62 811-6060-0518).'
    : phoneCheck.e164
      ? phoneCheck.isMobile
        ? `Disimpan sebagai ${phoneCheck.e164}`
        : `Disimpan sebagai ${phoneCheck.e164} — bukan nomor HP, invoice tidak bisa dikirim via WhatsApp.`
      : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={asRequest ? 'Ajukan perubahan customer' : isEdit ? 'Edit customer' : 'New customer'}
      description={
        asRequest
          ? 'Kamu tidak punya hak ubah customer. Perubahan diterapkan setelah disetujui admin dari menu Persetujuan.'
          : isEdit
            ? 'Update data customer. Snapshot di invoice lama tetap tersimpan.'
            : 'Tambah customer baru ke master data.'
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="customer-form"
            variant="primary"
            loading={isSubmitting || create.isPending || update.isPending || requestEdit.isPending}
            disabled={asRequest && !!pendingRequest}
          >
            {asRequest ? 'Ajukan perubahan' : isEdit ? 'Save changes' : 'Create customer'}
          </Button>
        </>
      }
    >
      <form id="customer-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {asRequest && pendingRequest && (
          <div role="status" className="p-3 rounded border border-warning/30 bg-warning/5 text-caption text-ink-subtle">
            Sudah ada permintaan perubahan untuk customer ini yang <b>menunggu persetujuan</b>
            {pendingRequest.requestedByName ? ` (diajukan ${pendingRequest.requestedByName})` : ''}.
            Tunggu keputusannya sebelum mengajukan lagi.
          </div>
        )}
        {submitError && (
          <p role="alert" className="text-caption text-danger">{submitError}</p>
        )}
        <Input
          label="Name"
          placeholder="Contoh: Budi Santoso"
          required
          error={errors.name?.message}
          {...register('name', { required: 'Nama wajib diisi' })}
        />

        <Input
          label="Company (opsional)"
          placeholder="Contoh: PT Maju Mundur"
          hint="Boleh kosong untuk customer perorangan."
          error={errors.company?.message}
          {...register('company')}
        />

        <Input
          label="Phone"
          placeholder="0811 6060 0518"
          required
          error={errors.phone?.message}
          hint={phoneHint}
          {...register('phone', {
            required: 'No telp wajib diisi',
            validate: (v) => checkPhone(v).problem ?? true,
            // Rewrites the field to canonical form as soon as focus leaves, so
            // what the operator sees is what is stored. Pasting straight from
            // WhatsApp ("+62 811-6060-0518") or typing "0811..." both land on
            // the same value.
            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
              const { e164 } = checkPhone(e.target.value);
              if (e164) setValue('phone', formatPhone(e164), { shouldValidate: true });
            },
          })}
        />

        <Input
          label="Email (opsional)"
          placeholder="nama@perusahaan.com"
          type="email"
          error={errors.email?.message}
          {...register('email', {
            pattern: {
              value: emailRegex,
              message: 'Format email tidak valid',
            },
          })}
        />

        <Input
          label="NIK / NPWP (opsional)"
          placeholder="3201234567890001 atau 09.254.294.3-407.000"
          hint="NIK untuk perorangan, NPWP untuk badan usaha — isi salah satu."
          error={errors.taxId?.message}
          {...register('taxId')}
        />

        <Textarea
          label="Alamat (opsional)"
          rows={2}
          placeholder="Jl. Merdeka No. 5, Jakarta Pusat"
          {...register('address')}
        />
      </form>
    </Modal>
  );
}
